import { useState, type FormEvent } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import Logo from './Logo';
import { categories } from '@/mocks/articles';

const navItems = [
  { to: '/', label: '首页', end: true },
  { to: '/community', label: '社区' },
  { to: '/favorites', label: '我的收藏' },
  { to: '/about', label: '关于我们' },
];

export default function Navbar() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState('');
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    const term = keyword.trim();
    if (!term) return;
    navigate(`/search?q=${encodeURIComponent(term)}`);
    setMobileOpen(false);
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
      isActive
        ? 'text-primary-700 bg-primary-50'
        : 'text-foreground-600 hover:text-primary-700 hover:bg-background-100'
    }`;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/90 backdrop-blur-md">
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <Logo />
            <nav className="hidden lg:flex items-center gap-1">
              <NavLink to="/" end className={linkClass}>
                首页
              </NavLink>
              <div
                className="relative"
                onMouseEnter={() => setCategoryOpen(true)}
                onMouseLeave={() => setCategoryOpen(false)}
              >
                <button
                  type="button"
                  onClick={() => setCategoryOpen((prev) => !prev)}
                  className="flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md text-foreground-600 hover:text-primary-700 hover:bg-background-100 transition-colors whitespace-nowrap"
                >
                  分类
                  <i className={`ri-arrow-down-s-line transition-transform ${categoryOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {categoryOpen ? (
                  <div className="absolute left-0 top-full pt-2 w-64">
                    <div className="rounded-lg border border-background-200 bg-background-50 p-2 shadow-sm">
                      {categories.map((cat) => (
                        <Link
                          key={cat.key}
                          to={`/category/${cat.key}`}
                          onClick={() => setCategoryOpen(false)}
                          className="flex items-center gap-3 rounded-md px-3 py-2 hover:bg-background-100 transition-colors"
                        >
                          <span className="w-8 h-8 rounded-md bg-primary-50 text-primary-600 flex items-center justify-center">
                            <i className={`${cat.icon} text-base`}></i>
                          </span>
                          <span>
                            <span className="block text-sm font-medium text-foreground-900">{cat.name}</span>
                            <span className="block text-xs text-foreground-400 line-clamp-1">{cat.desc}</span>
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
              {navItems.slice(1).map((item) => (
                <NavLink key={item.to} to={item.to} className={linkClass}>
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-2">
            <form
              onSubmit={handleSearch}
              className="hidden md:flex items-center gap-2 rounded-full border border-background-200 bg-background-100 px-3 py-1.5 w-56 lg:w-64 focus-within:border-primary-400 focus-within:bg-background-50 transition-colors"
            >
              <i className="ri-search-line text-foreground-400 text-sm"></i>
              <input
                type="text"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索资讯或话题"
                className="w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 outline-none"
              />
            </form>
            <Link
              to="/search"
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 transition-colors"
              aria-label="搜索"
            >
              <i className="ri-search-line text-lg"></i>
            </Link>
            <button
              type="button"
              onClick={() => setMobileOpen((prev) => !prev)}
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 transition-colors"
              aria-label="打开菜单"
            >
              <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen ? (
        <div className="lg:hidden border-t border-background-200 bg-background-50 px-4 py-4 animate-soft-in">
          <form
            onSubmit={handleSearch}
            className="flex items-center gap-2 rounded-full border border-background-200 bg-background-100 px-3 py-2 mb-4"
          >
            <i className="ri-search-line text-foreground-400 text-sm"></i>
            <input
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索资讯或话题"
              className="w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 outline-none"
            />
          </form>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {categories.map((cat) => (
              <Link
                key={cat.key}
                to={`/category/${cat.key}`}
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 rounded-md bg-background-100 px-3 py-2.5 text-sm text-foreground-800"
              >
                <i className={`${cat.icon} text-primary-600`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
          <nav className="flex flex-col">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setMobileOpen(false)}
                className={linkClass}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      ) : null}
    </header>
  );
}